import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { ArrowRight, Waves, Trees, Calendar, Tent } from "lucide-react";
import { cdn, srcSet } from "@/lib/img";

const IMG_SITE_ONE = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/kzbxkx3n_IMG_8994.jpeg";
const IMG_SITE_TWO = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/0zj0zjrb_IMG_7577.jpeg";

const SITES = [
  {
    id: "site-one",
    kicker: "Ready First",
    name: "Site One",
    image: { src: IMG_SITE_ONE, alt: "The new stone-and-gravel access road cutting to Site One" },
    stats: [
      { icon: Trees, label: "Land", value: "3.5 acres" },
      { icon: Waves, label: "Waterfront", value: "175 ft" },
      { icon: Calendar, label: "Opens", value: "2027 season" },
    ],
    highlights: [
      "First site online — all construction effort this year",
      "Shared access road, wooded buffer from Site Two",
      "Private trail to the viewing deck, then down to the shore",
    ],
    pricing: [
      { trailers: 1, rate: 90 },
      { trailers: 2, rate: 120 },
      { trailers: 3, rate: 150 },
    ],
    status: "building",
  },
  {
    id: "site-two",
    kicker: "What's Next",
    name: "Site Two",
    image: { src: IMG_SITE_TWO, alt: "The rocky Bay of Fundy shoreline along the Site Two waterfront" },
    stats: [
      { icon: Trees, label: "Land", value: "15 acres" },
      { icon: Waves, label: "Waterfront", value: "550 ft" },
      { icon: Calendar, label: "Opens", value: "Longer-term" },
    ],
    highlights: [
      "Four times the land — room to spread out, real wilderness feel",
      "Deeper into the property, more privacy from the road",
      "Bigger waterfront footprint, multiple access points to the shore",
    ],
    pricing: [
      { trailers: 1, rate: 90 },
      { trailers: 2, rate: 120 },
      { trailers: 3, rate: 150 },
    ],
    status: "planned",
  },
];

function PricingTable({ pricing, testid }) {
  return (
    <div className="mt-5 border-t border-wc-teal/20 pt-5" data-testid={testid}>
      <p className="font-body text-wc-slate/80 text-[11px] uppercase tracking-[0.2em] mb-3">Nightly rate</p>
      <div className="grid grid-cols-3 gap-2">
        {pricing.map(p => (
          <div key={p.trailers}
            className="flex flex-col items-center justify-center rounded-sm border border-wc-teal/20 bg-white/60 py-3 px-2"
            data-testid={`${testid}-tier-${p.trailers}`}>
            <p className="font-display text-wc-forest text-2xl font-semibold leading-none">
              ${p.rate}
            </p>
            <p className="font-body text-wc-slate text-[11px] mt-1.5 leading-tight text-center">
              {p.trailers} {p.trailers === 1 ? "trailer" : "trailers"}
            </p>
          </div>
        ))}
      </div>
      <p className="font-accent italic text-wc-slate text-xs mt-3 flex items-center gap-1.5">
        <Tent className="w-3 h-3" />
        Trailers & RVs only — no tents (no bathroom facilities on site)
      </p>
      <p className="font-body text-wc-slate/70 text-xs mt-1">Two-night minimum.</p>
    </div>
  );
}

function SiteCard({ site, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start"
      data-testid={`site-card-${site.id}`}
    >
      {/* Image */}
      <div className={`${index % 2 === 1 ? "lg:order-2" : ""}`}>
        <div className="rounded-sm overflow-hidden aspect-[4/3] shadow-sm border border-wc-forest/10">
          <img
            src={cdn(site.image.src, 1100)}
            srcSet={srcSet(site.image.src)}
            sizes="(max-width: 1024px) 100vw, 45vw"
            alt={site.image.alt}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover wc-img"
          />
        </div>
      </div>

      {/* Content */}
      <div>
        <p className="font-accent italic text-wc-teal text-sm mb-2">{site.kicker}</p>
        <h3 className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold mb-5 leading-[1.1]">
          {site.name}
        </h3>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mb-5 border-y border-wc-teal/15 py-4">
          {site.stats.map((s) => (
            <div key={s.label} className="flex flex-col" data-testid={`${site.id}-stat-${s.label.toLowerCase()}`}>
              <div className="flex items-center gap-1.5 text-wc-teal mb-1">
                <s.icon className="w-3.5 h-3.5" />
                <span className="font-body text-[10px] uppercase tracking-[0.15em] opacity-80">{s.label}</span>
              </div>
              <p className="font-display text-wc-forest text-base sm:text-lg font-semibold leading-tight">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Highlights */}
        <ul className="space-y-2 mb-2">
          {site.highlights.map((h, i) => (
            <li key={i} className="flex gap-3 font-body text-wc-slate text-sm sm:text-base leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-wc-teal flex-shrink-0" aria-hidden="true" />
              {h}
            </li>
          ))}
        </ul>

        {/* Pricing */}
        <PricingTable pricing={site.pricing} testid={`${site.id}-pricing`} />

        {/* CTA */}
        <Link
          to={`/waitlist?site=${site.id}`}
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-wc-forest text-wc-cream font-body text-sm tracking-wide rounded-full hover:bg-wc-fern transition-colors"
          data-testid={`${site.id}-waitlist-cta`}
        >
          Join the {site.name} waitlist <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.article>
  );
}

export default function Sites() {
  return (
    <section id="sites" className="wc-section bg-wc-cream" data-testid="sites-section">
      <div className="max-w-6xl mx-auto">
        <header className="max-w-3xl mb-14">
          <p className="font-accent italic text-wc-teal text-base mb-3">Two Sites</p>
          <h2 className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.15] mb-4">
            One road in. Two places to park your life for a few nights.
          </h2>
          <p className="font-body text-wc-slate text-base sm:text-lg leading-relaxed">
            Site One is what we're finishing now — it'll be the first to open. Site Two is the larger parcel further down the shared road, opening a year later. Same Bay of Fundy. Same zero-light-pollution sky. Different scale.
          </p>
        </header>

        <div className="space-y-16 sm:space-y-20">
          {SITES.map((site, i) => (
            <SiteCard key={site.id} site={site} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
